import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

// Register required Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const getAgeLabel = (value) => {
  switch (value) {
    case "A00":
      return "All Ages (14-99)";
    case "A01":
      return "14-18";
    case "A02":
      return "19-21";
    case "A03":
      return "22-24";
    case "A04":
      return "25-34";
    case "A05":
      return "35-44";
    case "A06":
      return "45-54";
    case "A07":
      return "55-64";
    case "A08":
      return "65-99";
    default:
      return "Unknown";
  }
};

const SpecificAgeGroupEmployeeChart = (props) => {
  const pdfRef = useRef();
  const { data } = props;
  // Ensure data is valid and not empty
  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }
  // Extract years dynamically from the first data object
  const years = Object.keys(data[0]).filter((key) => key.startsWith("year_"));

  // Prepare data for the stacked bar chart
  const chartData = {
    labels: years.map((year) => year.replace("year_", "")), // Format years for display
    datasets: data.map((item, index) => ({
      label: getAgeLabel(item.agegrp_label), // Age group label
      data: years.map((year) => parseInt(item[year] || 0)), // Employee data for each year
      // backgroundColor: `rgba(${54 + index * 50}, ${162 - index * 30}, ${
      //   235 - index * 20
      // }, 0.7)`,
      backgroundColor: index % 2 === 0 ? "rgb(1, 76, 178)" : "rgb(203, 12, 12)",

      borderColor: `rgba(${54 + index * 50}, ${162 - index * 30}, ${
        235 - index * 20
      }, 1)`,
      borderWidth: 1,
    })),
  };

  // Chart options
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: "Employee Data by Age Group (Stacked Bar Chart)",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString("en-US")}`;
          },
        },
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Year",
        },
        stacked: true, // Enable stacking on the X-axis
      },
      y: {
        stacked: true, // Enable stacking
        title: {
          display: true,
          text: "Number of Employees",
        },
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value.toLocaleString("en-US");
          },
        },
      },
    },
  };

  // PDF Generation function
  const downloadPDF = () => {
    const input = pdfRef.current;
    const originalWidth = input.style.width;
    input.style.width = "1100px"; // Temporarily increase width for better PDF capture

    html2canvas(input, { scale: 3 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Add header and footer
      // Header
      pdf.addImage(utahData, "PNG", 60, 10, 32, 16); // Logo
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });

      pdf.setFontSize(12);
      pdf.setLineWidth(0.5);
      pdf.line(10, 35, 200, 35); // Line after header

      // Add main content
      const headerHeight = 38;
      pdf.addImage(imgData, "PNG", 5, headerHeight, imgWidth, imgHeight);

      // Footer
      pdf.line(10, 260, 200, 260); // Line before footer
      const footerStartY = 265;
      pdf.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdf.setFontSize(10);
      pdf.setTextColor(0, 122, 255);
      pdf.text("Built by", 55, footerStartY + 10);
      pdf.addImage(bodex, "PNG", 68, footerStartY + 4, 44, 10);
      pdf.text("using", 112, footerStartY + 10);
      pdf.addImage(kpi, "PNG", 125, footerStartY, 20, 20);
      pdf.text(" 20250916.v3.198", 148, footerStartY + 10);

      // Date and URL
      const currentDate = new Date().toLocaleDateString();
      pdf.setFontSize(10);
      pdf.text(`Downloaded on: ${currentDate}`, 150, 295);
      const currentURL = window.location.href;
      pdf.text(`Downloaded from: ${currentURL}`, 10, 295);

      pdf.save("specific-age-group-employee-chart.pdf");
      input.style.width = originalWidth; // Reset width
    });
  };

  return (
    <>
      {" "}
      <div ref={pdfRef} className="content-to-export">
        <div style={{ padding: "20px" }}>
          <h2>Specific Age Group Employee Data</h2>
          {/* Table Section */}
          <div className="overflow-x-auto">
            <table className="table table-striped bg-white">
              <thead>
                <tr>
                  <th>Age Group</th>
                  <th>2018</th>
                  <th>2019</th>
                  <th>2020</th>
                  <th>2021</th>
                  <th>2022</th>
                  <th>2023</th>
                  <th>2024</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index}>
                    <td>{getAgeLabel(row.agegrp_label)}</td>
                    <td>{Number(row.year_2018 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.year_2019 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.year_2020 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.year_2021 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.year_2022 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.year_2023 || 0).toLocaleString("en-US")}</td>
                    <td>{Number(row.year_2024 || 0).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div
          style={{
            width: "90%",
            maxWidth: "800px",
            margin: "auto",
            overflowX: "auto",
          }}
        >
          {" "}
          <div
            style={{
              width: "100%", // Takes 100% width of the parent div, but will scroll if needed
              minWidth: "600px", // Ensures a minimum width, enabling scroll when the chart exceeds screen size
              background: "#fff",
            }}
          >
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>
      <br />
      <div className="flex justify-center">
        <button
          onClick={downloadPDF}
          className="download-btn bg-red-800 hover:bg-red-950 p-2 text-white rounded-lg flex items-center justify-between"
        >
          <span>Download as PDF </span>
          <span>
            <img className="w-[2rem]" src={pdf} onClick={downloadPDF} />
          </span>
        </button>
      </div>
    </>
  );
};

export default SpecificAgeGroupEmployeeChart;
